/**
 * Showcase booths for the /new hero, loaded once and shared.
 *
 * The headline slug and the hero device must always show the SAME booth —
 * a URL reading one name over a phone showing another reads as decoration
 * rather than a real booth. So the cursor lives here, not in either component.
 *
 * Everything degrades: an empty feed means /new renders its static headline
 * and its text starter chips, exactly as before the showcase existed.
 */

import { useEffect, useRef, useState } from 'react'
import { fetchShowcaseBooths, type ShowcaseBooth } from './onboardingApi'

/** Long enough to read the name and take in the screen; short enough to feel live. */
const ROTATE_MS = 4200

interface Showcase {
  booths: ShowcaseBooth[]
  /** The booth currently being shown, or null while empty/loading. */
  current: ShowcaseBooth | null
  index: number
  /** Freezes rotation — used while the visitor is typing their own slug. */
  setPaused: (paused: boolean) => void
}

export function useShowcase(): Showcase {
  const [booths, setBooths] = useState<ShowcaseBooth[]>([])
  const [index, setIndex] = useState(0)
  const [paused, setPaused] = useState(false)
  const reduced = useRef(false)

  useEffect(() => {
    reduced.current =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches

    let cancelled = false
    void fetchShowcaseBooths().then((items) => {
      if (!cancelled) setBooths(items)
    })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    // One booth doesn't rotate, and a visitor who asked for reduced motion
    // gets the first booth and nothing moving.
    if (paused || booths.length < 2 || reduced.current) return
    const t = setInterval(() => setIndex((i) => (i + 1) % booths.length), ROTATE_MS)
    return () => clearInterval(t)
  }, [paused, booths.length])

  return {
    booths,
    current: booths[index] ?? null,
    index,
    setPaused
  }
}
